import { SlackConnectionStatus } from '../../../reducers/integrationsReducer';
import { AutomationType } from '../../../types/enums/AutomationType';
import { AutomationDataActionType, AutomationTypes } from '../../../types/enums/AutomationTypes';
import { getIsScheduleValid, validateAnAction } from '../helper';

export const validateTrigger = (
  automationJson: DeepPartial<AutomationStructureNew>,
  isObjectless?: boolean,
) => {
  if (
    (!automationJson.objectName && !isObjectless) ||
    !automationJson.automationDetails?.triggerType
  ) {
    return false;
  }
  const { stageEnter, stageExit, fieldUpdate, dateArrive, timeInStep, schedule } =
    automationJson.automationDetails?.automationParams || {};
  switch (automationJson.automationDetails?.triggerType) {
    case AutomationTypes.StageEnter:
      return stageEnter?.toStage && automationJson.automationDetails.funnelId;
    case AutomationTypes.StageExit:
      return (
        stageExit?.fromStage && stageExit?.toStage && automationJson.automationDetails.funnelId
      );
    case AutomationTypes.FieldUpdate:
      return fieldUpdate?.operator && fieldUpdate?.operatorValue;
    case AutomationTypes.DateArrive:
      return (
        dateArrive?.relativeTime &&
        dateArrive?.sourceField?._leadingFieldLabels &&
        dateArrive?.sourceField?._leadingFieldLabels.length > 1 &&
        (dateArrive.relativeTime !== 'exact'
          ? dateArrive.offsetValue && dateArrive.offsetType
          : true)
      );
    case AutomationTypes.TimeInStep:
      return (
        timeInStep?.offsetType &&
        timeInStep?.stage?.stageName &&
        timeInStep?.stage?._stageId &&
        timeInStep?.offsetValue &&
        automationJson.automationDetails.funnelId
      );
    case AutomationTypes.Scheduled:
      const isScheduleValid = getIsScheduleValid(schedule);
      const isDataTypeReport =
        automationJson.automationDetails?.getDataAction?.actionType ===
        AutomationDataActionType.REPORT_RESULTS;
      let isValidData = false;
      if (isDataTypeReport) {
        isValidData =
          !!(automationJson.automationDetails?.getDataAction as AutomationDataActionReportResult)
            ?.actionParams?.reportName &&
          !!(automationJson.automationDetails?.getDataAction as AutomationDataActionReportResult)
            ?.actionParams?.columnIdFieldLabel;
      } else {
        const condition = (
          automationJson.automationDetails?.getDataAction as AutomationDataActionQueryResult
        )?.actionParams?.criteria;
        isValidData =
          condition?.criteria?.length > 0 &&
          condition?.criteria?.[0]?._fieldIds?.length > 0 &&
          !!condition?.criteriaLogic;
      }
      if (
        automationJson.type === AutomationType.ScheduledReport ||
        automationJson.type === AutomationType.ScheduledJob
      ) {
        isValidData = true;
      }
      return isScheduleValid && isValidData;
  }
  return true;
};

export const getIsAutomationsFormValid = ({
  automationJson,
  slackStatus,
  isObjectless,
  anyFormulaEditorOpenExists,
  anyOtherObjectEditorOpenExists,
}: {
  automationJson: DeepPartial<AutomationStructureNew>;
  slackStatus: SlackConnectionStatus;
  isObjectless?: boolean;
  anyFormulaEditorOpenExists?: boolean;
  anyOtherObjectEditorOpenExists?: boolean;
}) => {
  const validateActions = (_actions?: (DeepPartial<AutomationAction> | undefined)[]) => {
    const _currActions = _actions ?? automationJson.automationDetails?.actions;
    if (_currActions?.length === 0) {
      return false;
    }

    const hasAnyInvalidAction = _currActions?.find(
      (action) =>
        action &&
        !validateAnAction({
          action,
          slackStatus,
          objectName: automationJson.objectName,
        }),
    );
    return !hasAnyInvalidAction;
  };

  const validateDelay = () => {
    if (automationJson.type === AutomationType.MarketingAttributionModel) {
      return true;
    }
    if (automationJson.automationDetails?.delay) {
      if (
        automationJson.automationDetails?.delay.offsetType &&
        automationJson.automationDetails?.delay.offsetValue
      ) {
        return true;
      }
    } else {
      return true;
    }
  };

  const isTriggerValid = validateTrigger(automationJson, isObjectless);
  const areActionsValid = validateActions();
  const isDelayValid = validateDelay();
  const isNameValid = automationJson.name && automationJson.name.length > 0;

  return (
    isNameValid &&
    isTriggerValid &&
    areActionsValid &&
    !anyFormulaEditorOpenExists &&
    isDelayValid &&
    !anyOtherObjectEditorOpenExists
  );
};
